/**
 * Home page sections — extracted verbatim from Home.jsx for maintainability.
 * Each export is one <section> of the landing page.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, ShieldCheck, Cpu, Users, GitBranch, LineChart, Download, Landmark, Building2, HeartPulse, ShoppingBag, Zap, Factory, Scale, Radio, GraduationCap, Linkedin } from "lucide-react";
import HeroVisual from "../../components/HeroVisual";
import Lifecycle from "../../components/Lifecycle";
import StarMotif from "../../components/StarMotif";
import CyclingPhases from "../../components/CyclingPhases";
import { services, outcomes, differentiators, priorityOffers, industries } from "../../data/services";
import { ENGINE, FAMILY_COUNT } from "../../data/family360";
import { BRAND } from "../../data/brand";

const ICONS = { Compass, ShieldCheck, Cpu, Users, GitBranch, LineChart };
const INDUSTRY_ICONS = { Landmark, Building2, HeartPulse, ShoppingBag, Zap, Factory, Scale, Radio, GraduationCap };

export const HeroSection = () => (
  <section className="relative ek-gradient-dark text-white overflow-hidden" data-testid="hero-section">
    <div className="ek-star-watermark" />
    <div className="ek-vignette-top" />
    <div className="ek-container relative z-10 py-20 md:py-28 lg:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <div className="ek-eyebrow text-[#A179FA] ek-fade-up" data-testid="hero-eyebrow">EKSELENS360 · Reinvention assurance</div>
          <h1 className="ek-heading text-5xl md:text-6xl lg:text-7xl text-white mt-5 ek-fade-up" style={{ animationDelay: "60ms" }} data-testid="hero-headline">
            Technology-driven transformation. <br />
            <span className="text-[#A179FA]">Practical delivery.</span> Measurable value.
          </h1>
          <p className="text-[#A179FA] font-light italic text-lg md:text-xl mt-5 ek-fade-up" style={{ animationDelay: "90ms" }} data-testid="hero-tagline">
            Proof, generated as the work is done — not audited after.
          </p>
          <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-6 max-w-2xl ek-fade-up" style={{ animationDelay: "120ms" }} data-testid="hero-subheading">
            Ekselens builds the <span className="text-white font-normal">system of proof</span> for enterprise reinvention — so every decision is traceable, every artefact defensible, and value is realised, not just promised.
          </p>
          <div className="flex flex-wrap gap-4 mt-9 ek-fade-up" style={{ animationDelay: "200ms" }}>
            <Link to="/contact" className="ek-btn ek-btn-primary" data-testid="hero-primary-cta">
              Book a discovery conversation <ArrowRight size={16} />
            </Link>
            <Link to="/system-of-proof" className="ek-btn ek-btn-outline-light" data-testid="hero-secondary-cta">
              Assess your system of proof
            </Link>
          </div>
          <p className="text-zinc-400 text-[13px] mt-3 ek-fade-up" style={{ animationDelay: "240ms" }} data-testid="hero-cta-promise">
            Free · 30 minutes · Leave with three prioritised next steps.
          </p>
          {BRAND.linkedinUrl ? (
            <a href={BRAND.linkedinUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-5 text-zinc-300 hover:text-white text-[13px] transition-colors ek-fade-up" style={{ animationDelay: "300ms" }} data-testid="hero-linkedin-pill">
              <Linkedin size={14} className="text-[#A179FA]" />
              <span className="border-b border-zinc-600 hover:border-white pb-0.5">Follow Ekselens Consulting on LinkedIn</span>
            </a>
          ) : null}
          <CyclingPhases />
        </div>
        <div className="lg:col-span-5 ek-fade-up" style={{ animationDelay: "280ms" }}>
          <HeroVisual />
        </div>
      </div>
    </div>
  </section>
);

export const OutcomesSection = () => (
  <section className="bg-white ek-section" data-testid="outcomes-section">
    <div className="ek-container">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-5">
          <div className="ek-eyebrow">What we help organisations do</div>
          <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black">From ambition <br /> to action.</h2>
        </div>
        <div className="lg:col-span-7">
          <p className="ek-body text-lg max-w-2xl">
            Ekselens helps organisations move from ambition to action. Whether you need to assess AI readiness, improve project delivery, redesign services, strengthen governance, optimise suppliers, improve data insight or realise benefits from transformation, we provide practical tools, structured methods and client-ready outputs.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 ek-stagger">
        {outcomes.map((o, i) => {
          const Icon = ICONS[o.icon];
          return (
            <div key={o.title} className="bg-white p-8 lg:p-10 group" data-testid={`outcome-card-${i}`}>
              <div className="w-10 h-10 flex items-center justify-center bg-[#F2F2F3] text-[#7737F6] mb-6 group-hover:bg-[#7737F6] group-hover:text-white transition-colors">
                {Icon ? <Icon size={20} strokeWidth={1.5} /> : null}
              </div>
              <div className="text-xs font-semibold tracking-[0.18em] text-zinc-500 uppercase">0{i + 1}</div>
              <h3 className="ek-heading text-xl md:text-2xl mt-2 text-black">{o.title}</h3>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export const ServicesSection = () => (
  <section className="bg-[#F2F2F3] ek-section" data-testid="services-section">
    <div className="ek-container">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-7">
          <div className="ek-eyebrow">Our services</div>
          <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black">One integrated consulting approach.</h2>
        </div>
        <div className="lg:col-span-5 flex lg:items-end">
          <p className="ek-body text-lg">
            Nine integrated services connected by the EKSELENS360 approach — designed to be applied individually or as a coordinated programme.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ek-stagger">
        {services.slice(0, 9).map((s, i) => (
          <Link key={s.slug} to={`/services/${s.slug}`} className="ek-card block group" data-testid={`service-card-${s.slug}`}>
            <div className="flex items-start justify-between mb-5">
              <div className="text-xs font-semibold tracking-[0.18em] text-zinc-500 uppercase">0{i + 1}</div>
              <StarMotif size={18} color="#7737F6" className="opacity-70 group-hover:opacity-100 transition-opacity" />
            </div>
            <h3 className="ek-heading text-xl md:text-[22px] text-black leading-snug">{s.name}</h3>
            <p className="ek-body text-[15px] mt-3">{s.short}</p>
            <div className="mt-6 inline-flex items-center gap-2 text-[#7737F6] text-sm font-medium">Learn more <ArrowRight size={14} /></div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export const Ekselens360Section = () => (
  <section className="bg-white ek-section relative" data-testid="ekselens360-section">
    <div className="ek-container relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-5">
          <div className="ek-eyebrow">The System of Proof approach</div>
          <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black">Discover → Optimise. <br /> One integrated lifecycle.</h2>
        </div>
        <div className="lg:col-span-7">
          <p className="ek-body text-lg max-w-2xl">
            Everything sits under the <strong>System of Proof</strong> — our thought-leadership and
            evidence discipline. It’s delivered through two methodologies: <strong>EKSELENS360</strong>,
            the function view (twelve specialist lenses for every capability), and <strong>Enterprise360</strong>,
            the value-stream view (the reinvention engine that assembles them into one tailored blueprint).
          </p>
          <Link to="/the-360-family" className="mt-5 inline-flex items-center gap-2 text-[#7737F6] text-sm font-medium hover:underline" data-testid="ekselens360-family-link">
            Explore the 360 family <ArrowRight size={14} />
          </Link>
        </div>
      </div>
      <Lifecycle />
    </div>
  </section>
);

export const Enterprise360MomentSection = () => (
  <section className="bg-black text-white ek-section relative overflow-hidden" data-testid="enterprise360-moment-section">
    <div className="ek-star-watermark-left" />
    <div className="ek-container relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="ek-eyebrow text-[#A179FA]">Twelve lenses · one engine</div>
          <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-white">Meet ENTERPRISE360 — <br /> the reinvention engine.</h2>
          <p className="text-zinc-300 font-light text-lg leading-relaxed mt-6 max-w-2xl">
            {ENGINE.blurb} It pulls the twelve specialist lenses in as deep-dives — so the whole
            enterprise is reinvented function by function. We are changing how transformation is done.
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to={ENGINE.path} className="ek-btn ek-btn-primary" data-testid="home-enterprise360-cta">
              Explore ENTERPRISE360 <ArrowRight size={16} />
            </Link>
            <Link to="/the-360-family" className="ek-btn ek-btn-outline-light" data-testid="home-family-cta">
              See all {FAMILY_COUNT} toolsets
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="border border-white/15 bg-white/[0.04] p-8">
            <div className="grid grid-cols-3 gap-3 text-center">
              {[`${ENGINE.sectors}`, `${ENGINE.archetypes}`, "L1–L4"].map((n, i) => (
                <div key={i} className="border border-white/10 py-5">
                  <div className="text-[#A179FA] text-2xl font-semibold">{n}</div>
                  <div className="text-[10.5px] text-zinc-400 uppercase tracking-[0.12em] mt-1">
                    {["sectors", "archetypes", "process maps"][i]}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-zinc-400 font-light text-[14px] mt-6 leading-relaxed">
              Classify by archetype · apply sector & complexity overlays · assemble a
              function-by-function blueprint your board can fund.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const PriorityOffersSection = ({ onBrochure }) => (
  <section className="ek-gradient-dark-soft text-white ek-section relative overflow-hidden" data-testid="priority-offers-section">
    <div className="ek-star-watermark-left" />
    <div className="ek-container relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-7">
          <div className="ek-eyebrow text-[#A179FA]">Priority offers</div>
          <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-white">Six focused engagements <br /> to start with.</h2>
        </div>
        <div className="lg:col-span-5 flex lg:items-end">
          <p className="text-zinc-300 font-light text-lg leading-relaxed">
            Time-boxed, outcome-oriented engagements scoped to deliver immediate clarity, evidence and momentum.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ek-stagger">
        {priorityOffers.map((o, i) => (
          <div key={o.title} className="ek-card ek-card-dark group" data-testid={`priority-offer-${i}`}>
            <div className="text-xs font-semibold tracking-[0.18em] text-[#A179FA] uppercase">Offer 0{i + 1}</div>
            <h3 className="ek-heading text-xl md:text-[22px] text-white mt-3 leading-snug">{o.title}</h3>
            <div className="mt-6 space-y-4 text-[14px]">
              <div>
                <div className="text-[11px] font-semibold tracking-[0.18em] text-zinc-500 uppercase mb-1">Who it helps</div>
                <p className="text-zinc-200 font-light">{o.who}</p>
              </div>
              <div>
                <div className="text-[11px] font-semibold tracking-[0.18em] text-zinc-500 uppercase mb-1">Typical challenge</div>
                <p className="text-zinc-200 font-light">{o.challenge}</p>
              </div>
              <div>
                <div className="text-[11px] font-semibold tracking-[0.18em] text-zinc-500 uppercase mb-1">What Ekselens delivers</div>
                <p className="text-zinc-200 font-light">{o.delivers}</p>
              </div>
            </div>
            <Link to={`/services/${o.slug}`} state={{ from: "offer" }} className="mt-7 inline-flex items-center gap-2 text-[#A179FA] hover:text-white text-sm font-medium transition-colors" data-testid={`priority-offer-cta-${i}`}>
              Discuss this offer <ArrowRight size={14} />
            </Link>
            <button type="button" onClick={() => onBrochure({ slug: o.slug, title: o.title })} className="mt-3 inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium transition-colors" data-testid={`priority-offer-brochure-${i}`}>
              <Download size={14} /> Download brochure
            </button>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const WhyEkselensSection = () => (
  <section className="bg-[#F2F2F3] ek-section" data-testid="why-ekselens-section">
    <div className="ek-container">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-5">
          <div className="ek-eyebrow">Why Ekselens</div>
          <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black">Practical. <br /> Evidence-led. <br /> Outcome-focused.</h2>
        </div>
        <div className="lg:col-span-7">
          <p className="ek-body text-lg max-w-2xl">
            We focus on outputs clients can use: roadmaps, dashboards, maturity assessments, operating models, stakeholder plans, governance packs, benefits registers and implementation-ready recommendations.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 ek-stagger">
        {differentiators.map((d, i) => (
          <div key={d.title} className="bg-[#F2F2F3] p-8 lg:p-10 relative" data-testid={`differentiator-${i}`}>
            <div className="text-[11px] font-semibold tracking-[0.18em] text-[#7737F6] uppercase mb-3">Principle 0{i + 1}</div>
            <h3 className="ek-heading text-xl text-black leading-snug">{d.title}</h3>
            <p className="ek-body text-[14.5px] mt-3">{d.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const IndustriesSection = () => (
  <section className="bg-white ek-section" data-testid="industries-section">
    <div className="ek-container">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-5">
          <div className="ek-eyebrow">Industries</div>
          <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black">Industries <br /> we support.</h2>
        </div>
        <div className="lg:col-span-7 flex lg:items-end">
          <p className="ek-body text-lg max-w-2xl">
            We work across regulated, enterprise and public-sector markets — applying the same EKSELENS360 lifecycle to transformation, AI adoption, supplier optimisation, change and benefits realisation. Named references are introduced on request once mutual NDAs are in place.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 ek-stagger">
        {industries.map((ind, i) => {
          const Icon = INDUSTRY_ICONS[ind.icon];
          return (
            <div key={ind.name} className="bg-white p-8 lg:p-10 transition-colors hover:bg-[#FAF8FF]" data-testid={`industry-${i}`}>
              <div className="flex items-start justify-between mb-6">
                {Icon ? <Icon size={28} strokeWidth={1.4} className="text-[#7737F6]" /> : null}
                <span className="text-[10px] font-semibold tracking-[0.2em] text-zinc-500 uppercase">0{i + 1}</span>
              </div>
              <h3 className="ek-heading text-xl md:text-2xl text-black leading-tight">{ind.name}</h3>
              <div className="text-[12px] tracking-[0.04em] text-[#7737F6] mt-1 font-medium">{ind.short}</div>
              <p className="ek-body text-[14.5px] mt-4">{ind.blurb}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);
